import { useState } from 'react';
import { Box, TextField, Card, CardActions, Typography } from '@mui/material';
import { Tag, colors } from '@sweep-io/sweep-design';
import { useAutomationsApiFacade } from '../../apis/facades/useAutomationsApiFacade';
import { AutoTemplatesStruct } from '../../constants/automationTemplates';
import { PageTemplateActionButton } from '../pages/pages-templates/PageTemplateActionButton';
import { telemetry } from '../../telemetry';

interface AiAutomationCreationCardProps {
  crmOrgId: string;
  onSelectTemplate: (item: AutoTemplatesStruct) => void;
  dataByVariant: any;
}

export const AiAutomationCreationCard = ({
  crmOrgId,
  onSelectTemplate,
  dataByVariant,
}: AiAutomationCreationCardProps) => {
  const [description, setDescription] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { post_ai_create_automation } = useAutomationsApiFacade();

  const handleSubmit = async () => {
    if (!description.trim()) {
      return;
    }

    setIsLoading(true);
    try {
      const result = await post_ai_create_automation({
        description: description.trim(),
        crmOrgId,
      });

      onSelectTemplate(result);
      setDescription('');
    } catch (error: any) {
      telemetry.captureError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSubmit();
    }
  };

  const background = `linear-gradient(90deg, ${colors.blue[500]} 0%, ${colors.blue[500]} 100%)`;

  return (
    <Card
      variant="outlined"
      sx={{
        padding: '32px 24px 24px 24px',
        position: 'relative',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        '&::before': {
          position: 'absolute',
          content: '""',
          background,
          height: '8px',
          width: '100%',
          top: 0,
          left: 0,
        },
      }}
    >
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', height: '40px' }}>
        <Tag label="AI Powered" color={colors.blue[500]} />
      </Box>

      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ height: '48px' }}>
          <Typography variant="h2">
            Describe what you want your {dataByVariant.nameInMessageSingular} to do
          </Typography>
        </Box>

        <TextField
          fullWidth
          multiline
          rows={4}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={`Describe your ${dataByVariant.nameInMessageSingular}... For example: "When an Account record's Account Rating field is updated to 'Cold' , send an email using the 'All Company Info' template to all@company.com."`}
          disabled={isLoading}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: colors.grey[50],
              '&:hover': {
                backgroundColor: colors.grey[100],
              },
              '&.Mui-focused': {
                backgroundColor: 'white',
              },
            },
          }}
        />
      </Box>

      <CardActions
        sx={{
          mt: 'auto',
          pt: 2,
          '& >.MuiBox-root': {
            width: '100%',
          },
          '& button': {
            width: '100%',
          },
        }}
      >
        <PageTemplateActionButton
          onClick={handleSubmit}
          disabled={isLoading || !description.trim()}
        >
          {isLoading ? 'Creating...' : 'Create with AI'}
        </PageTemplateActionButton>
      </CardActions>
    </Card>
  );
};
